import { createClient } from './graphql'
import {
  fetchMeetings,
  fetchOrgUsers,
  fetchPulses,
  fetchTasksForPulse,
} from './queries'
import type { GetToken } from '~/config'
import { withDemoMeetings } from '~/dev/demoMeetings'
import type { Meeting, TaskLite, WorldSnapshot } from './types'

/**
 * One read of the org's current state: rooms (pulses), characters (people),
 * and a sampling of tasks per team room. This is the still frame the game
 * spawns from; live Pusher events animate the deltas afterward.
 */
export async function buildSnapshot(
  getToken: GetToken,
  organizationId: string,
): Promise<WorldSnapshot> {
  const token = await getToken()
  const client = createClient(token)

  const [pulses, people, meetings] = await Promise.all([
    fetchPulses(client, organizationId),
    fetchOrgUsers(client, organizationId),
    fetchMeetings(client, organizationId).catch((): Meeting[] => []),
  ])

  // Pull tasks only for team rooms (where work visibly lives), capped so a big
  // org doesn't fan out into dozens of requests on first load. Memberless
  // pulses never become rooms (don't waste cap slots on them) and the busiest
  // rooms deserve the slots — same ordering the wall sweep uses.
  const teamPulses = pulses
    .filter((p) => p.category === 'TEAM' && p.memberUserIds.length > 0)
    .sort((a, b) => {
      const ta = a.latestUpdate ? Date.parse(a.latestUpdate) || 0 : 0
      const tb = b.latestUpdate ? Date.parse(b.latestUpdate) || 0 : 0
      return tb - ta
    })
    .slice(0, 8)
  const tasksByPulse: Record<string, TaskLite[]> = {}
  await Promise.all(
    teamPulses.map(async (p) => {
      try {
        tasksByPulse[p.id] = await fetchTasksForPulse(client, organizationId, p.id)
      } catch {
        tasksByPulse[p.id] = []
      }
    }),
  )

  return withDemoMeetings({ organizationId, pulses, people, tasksByPulse, meetings })
}
